import React from "react"
import { graphql } from "gatsby"
import { Box, Flex } from "@singita/components"
import { Link } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import RichTextRenderer from "../components/RichTextRenderer"
import {
  Meta,
  Heading,
  Text,
  GridContainer,
  Section,
  Card,
  Container,
  Divider,
  Author,
  ActionBar,
  Button,
} from "@singita/components"
import {
  generateBlogSlug,
  generateMagazineSlug,
  getMetaDescription,
  createSlug,
} from "../common/utils"
import SocialSharing from "../components/SocialSharing"
import HeroWrapper from "../modules/HeroWrapper"
import GridItem from "../components/GridItem"
import Main from "gatsby-singita-theme/src/components/Main"
import { useActionBar, useSeo } from "gatsby-singita-theme/src/hooks"
import HelmetWrapper from "gatsby-singita-theme/src/components/Helmet"
import {
  TEMPLATE_TYPES,
  ARTICLE_TYPES,
} from "gatsby-singita-theme/src/common/constants"
import ImageWrapper from "../modules/ImageWrapper"
import { getSrc } from "gatsby-plugin-image"

const ARTICLE_TYPE_MAP = {
  [ARTICLE_TYPES.BLOG]: "blog",
  [ARTICLE_TYPES.WILDLIFE]: "wildlife",
  [ARTICLE_TYPES.PRESS]: "press-room",
  [ARTICLE_TYPES.MAGAZINE]: "digital-magazine",
}

const DATA_MAP = {
  title: ({ type, headline, date }) => {
    return type === "Wildlife Report" ? `${headline}: ${date}` : headline
  },
  description: ({ content, description }) => {
    if (description?.description) {
      return description.description
    } else if (content?.raw) {
      return getMetaDescription(JSON.parse(content.raw))
    } else {
      return null
    }
  },
  image: ["image", "gatsbyImageData", "src"],
  altText: ["image", "description"],
  publishDate: ["publishDate"],
  author: ({ author }) => {
    return author ? author.name : "Author unknown"
  },
  type: ["type"],
  categories: ({ categories }) => {
    return categories ? categories.map(({ name }) => name).join(", ") : null
  },
  breadcrumb: ({ type }) => {
    return type ? ARTICLE_TYPE_MAP[type] : null
  },
}

// FlippingBook Component for Digital Magazine
const FlippingBookEmbed = ({ embeddedEntry }) => {
  const { source, headline, description, name, thumbnail } = embeddedEntry
  
  if (!source) {
    return (
      <Box
        bg="gray.50"
        p={[4]}
        borderRadius="8px"
        textAlign="center"
        mb={[4]}
      >
        <Text color="gray.500">
          Digital magazine content will be available soon.
        </Text>
      </Box>
    )
  }

  return (
    <Box mb={[5]}>
      {/* Description */}
      {description?.description && (
        <Box
          borderRadius="8px"
          textAlign="center"
          mb={[5]}
        >
          <Text>
            {description.description}
          </Text>
        </Box>
      )}
      {/* FlippingBook Embed */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: ['400px', '500px', '600px', '700px'],
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
          mb: [4]
        }}
      >
        <iframe
          src={source}
          width="100%"
          height="100%"
          frameBorder="0"
          allowFullScreen
          title={`${headline || name} - Digital Magazine`}
          style={{ border: 'none' }}
        />
      </Box>

      {/* Action Buttons */}
      <Flex justifyContent="center" alignItems="center" mb={[4]} flexWrap="wrap">
        <Button
          as="a"
          href={source}
          target="_blank"
          rel="noopener noreferrer"
          variant="primary"
          mr={[3]}
        >
          View Full Screen
        </Button>
        
        <Button
            as={Link}
            to="/digital-magazines/"
            variant="outline"
            colorScheme="brandBrown"
          >
            Back
          </Button>
      </Flex>
    </Box>
  )
}

function findFlippingBookEntries(content) {
  if (!content || !content.references) return []

  return content.references.filter(ref => {
    return ref.__typename === "ContentfulEmbed" && ref.contentful_id && ref.source
  })
}

function renderRelatedPost(post, index) {
  return (
    <GridItem index={index} columns={[1, 1, 2, 3]}>
      <Link to={generateBlogSlug(post.slug, post.year, post.month)}>
        <Card
          variant="standard"
          isLink={true}
          headline={post.name}
          meta={[
            { text: post.date, color: "textDark" },
            ...(post.categories && post.categories.length > 0
              ? [{ text: post.categories[0].name, color: "brandBrown" }]
              : []),
          ]}
          image={<ImageWrapper image={post.image} />}
        />
      </Link>
    </GridItem>
  )
}

// Digital Magazine specific rendering function
function renderRelatedMagazine(magazine, index) {
  return (
    <GridItem key={magazine.id} index={index} columns={[1, 1, 2, 3]}>
      <Link to={generateMagazineSlug(magazine.slug, magazine.year, magazine.month)}>
        <Card
          variant="standard"
          isLink={true}
          headline={magazine.name}
          meta={[
            { text: magazine.date, color: "textDark" },
          ]}
          image={<ImageWrapper image={magazine.image} />}
        />
      </Link>
    </GridItem>
  )
}

const ArticleTemplate = props => {
  const {
    data: { contentfulArticles, fallbackRelatedArticles },
  } = props
  const {
    headline,
    date,
    hero,
    categories,
    content,
    relatedArticles,
    structuredData,
    author,
    type,
  } = contentfulArticles

  const [actionbarRef, isMobile] = useActionBar()
  const seoData = useSeo(contentfulArticles, DATA_MAP, TEMPLATE_TYPES.ARTICLE)

  const hasRelatedArticles = relatedArticles && relatedArticles.length > 0
  const hasFallbackArticles =
    fallbackRelatedArticles.edges && fallbackRelatedArticles.edges.length > 0

  const isDigitalMagazine = type === "Digital Magazine"
  const flippingBookEntries = isDigitalMagazine ? findFlippingBookEntries(content) : []

  // Digital Magazine Layout
  if (isDigitalMagazine) {
    return (
      <Main {...props} codes={{}}>
        <HelmetWrapper {...seoData} />
        
        {/* Hero section for magazine */}
        {hero && hero.image && (
          <HeroWrapper imageRef={hero} subtitle={hero.caption} />
        )}
        
        <Container>
          {/* Magazine Header */}
          <Box maxWidth="900px" mx="auto" pt={[6]} pb={[4]} px={[2]}>
            <Box mb={[3]} textAlign="center">
              <Meta
                meta={[
                  { text: date, color: "textDark" },
                ]}
                size="body"
              />
            </Box>

            <Box mb={[4]} textAlign="center">
              <Heading size="h1" as="h1">
                {headline}
              </Heading>
            </Box>
          </Box>

          {/* FlippingBook Embeds */}
          <Box maxWidth="1000px" mx="auto" px={[2]} pb={[4]}>
            {flippingBookEntries.length > 0 ? (
              flippingBookEntries.map((entry, index) => (
                <FlippingBookEmbed 
                  key={entry.contentful_id || index} 
                  embeddedEntry={entry}
                />
              ))
            ) : (
              <Box
                bg="gray.50"
                p={[6]}
                borderRadius="8px"
                textAlign="center"
                mb={[6]}
              >
                <Text color="gray.500" mb={[3]}>
                  This digital magazine is not yet available for viewing.
                </Text>
                <Button
                  as={Link}
                  to="/digital-magazine/"
                  variant="primary"
                >
                  Browse Other Magazines
                </Button>
              </Box>
            )}
          </Box>

          {/* Related Digital Magazines */}
          {(hasRelatedArticles || hasFallbackArticles) && (
            <>
              <Divider mb={[4]} sx={{ opacity: 0.5 }} />
              <Box pb={[10]}>
                <Section headline="Other Digital Magazines">
                  <GridContainer
                    sx={{
                      gridTemplateColumns: [
                        "1fr",
                        "1fr",
                        "repeat(2, 1fr)",
                        "repeat(3, 1fr)",
                      ],
                      gap: [4],
                    }}
                  >
                    {hasRelatedArticles
                      ? relatedArticles
                          .filter(mag => mag.type === "Digital Magazine")
                          .map(renderRelatedMagazine)
                      : fallbackRelatedArticles.edges
                          .filter(({ node }) => node.type === "Digital Magazine")
                          .map(({ node }, index) => renderRelatedMagazine(node, index))}
                  </GridContainer>
                </Section>
              </Box>
            </>
          )}
        </Container>
      </Main>
    )
  }

  // Regular Article Layout (non-digital magazine)
  return (
    <Main {...props} codes={{}}>
      <HelmetWrapper {...seoData}/>
      {hero && hero.image && (
        <HeroWrapper imageRef={hero} subtitle={hero.caption} />
      )}
      <Box maxWidth="700px" mx="auto" pt={[10]} pb={[2]} px={[2]}>
        <Box mb={[3]}>
          <Meta
            meta={[
              { text: date, color: "textDark" },
              ...(categories || []).map(({ headline, hasPage }) => ({
                text: headline,
                color: "brandBrown",
                type: hasPage ? "link" : null,
                to: hasPage ? `/blog/${createSlug(headline)}/` : null,
              })),
            ]}
            size="body"
          />
        </Box>

        <ActionBar
          ref={actionbarRef}
          headline={
            isMobile
              ? null
              : structuredData
              ? structuredData.headline
              : headline
          }
          description={
            isMobile || !categories ? null : `in ${categories[0].headline}`
          }
          isSticky={true}
        >
          <Flex alignItems="center">
            {!isMobile && (
              <Box mr={[2]}>
                <Text ml={[0, 2]} size="small">
                  Share:
                </Text>
              </Box>
            )}
            {seoData && <SocialSharing {...seoData} />}
          </Flex>
        </ActionBar>
        <Box mb={[4]}>
          <Heading size="h1" as="h1">
            {props.pageContext.type === "Wildlife Report"
              ? `${headline}: ${date}`
              : headline}
          </Heading>
        </Box>
        <RichTextRenderer content={content} />
        {author && (
          <Link to={`/author/${createSlug(author.name)}/`}>
            <Author
              {...author}
              image={
                author.image ? (
                  <GatsbyImage
                    image={author.image.gatsbyImageData}
                  />
                ) : null
              }
            />
          </Link>
        )}
      </Box>
      {hasRelatedArticles || hasFallbackArticles ? (
        <Container>
          <Divider mb={[4]} sx={{ opacity: 0.5 }} />
          <Box pb={[10]}>
            <Section headline={`Related Stories`}>
              <GridContainer
                sx={{
                  gridTemplateColumns: [
                    "1fr",
                    "1fr",
                    "repeat(2, 1fr)",
                    "repeat(3, 1fr)",
                  ],
                  gap: [4],
                }}
              >
                {hasRelatedArticles
                  ? relatedArticles.map(renderRelatedPost)
                  : fallbackRelatedArticles.edges.map(({ node }, index) =>
                      renderRelatedPost(node, index)
                    )}
              </GridContainer>
            </Section>
          </Box>
        </Container>
      ) : null}
    </Main>
  )
}

export default ArticleTemplate

export const ArticleQuery = graphql`
  fragment RelatedArticle on ContentfulArticles {
    id
    name
    slug
    type
    date: publishDate(formatString: "MMMM YYYY")
    month: publishDate(formatString: "MM")
    year: publishDate(formatString: "YYYY")
    author {
      name
    }
    categories {
      name
    }
    image: previewImage {
      gatsbyImageData(
        layout: CONSTRAINED
        placeholder: NONE
        formats: [AUTO, WEBP]
        quality: 90
        width: 600
      )
      description
    }
  }

  query ArticleBySlug($slug: String!, $category: String, $type: String!) {
    contentfulArticles(slug: { eq: $slug }) {
      id
      name
      headline
      slug
      type
      date: publishDate(formatString: "MMMM YYYY")
      publishDate
      description {
        description
      }
      content {
        raw
        references {
          ...RichTextReferences
          ... on ContentfulEmbed {
              id
              contentful_id
              description {description}
              ...EmbedFields
            }
        }
      }
      hero {
        caption
        ...ImageFields
        image {
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: NONE
            formats: [AUTO, WEBP]
            quality: 90
            width: 1440
          )
        }
      }
      image: previewImage {
        gatsbyImageData(
          layout: CONSTRAINED
          placeholder: NONE
          formats: [AUTO, WEBP]
          quality: 90
          width: 600
          height: 390
        )
        description
      }
      categories {
        name
        headline
        hasPage
      }
      relatedArticles {
        ...RelatedArticle
      }
      structuredData {
        headline
        image {
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: NONE
            formats: [AUTO, WEBP]
            quality: 90
            width: 800
          )
        }
        description {
          description
        }
        altText
      }
      author {
        name
        role
        image {
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: NONE
            formats: [AUTO, WEBP]
            quality: 95
            height: 100
          )
        }
      }
    }
    fallbackRelatedArticles: allContentfulArticles(
      filter: {
        node_locale: { eq: "en-US" }
        categories: { elemMatch: { name: { eq: $category } } }
        slug: { ne: $slug }
        type: { eq: $type }
      }
      limit: 3
      sort: { publishDate: DESC }
    ) {
      edges {
        node {
          ...RelatedArticle
        }
      }
    }
  }
`
